import { useMemo, useState } from "react";
import { Badge, EmptyState, FormField, QueryStateBoundary } from "@/components/ui";
import { useDrinks } from "@/api/hooks/useDrinks";
import { SPIRIT_BASE_LABELS, type Drink } from "@/api/types/drink";

interface DrinkPickerListProps {
  onSelect: (drink: Drink) => void;
  excludeIds?: string[];
}

/**
 * Search-and-pick list over the active drink library — embedded inside
 * AddMenuRowDialog's "Library" tab (not a standalone Dialog itself, since
 * it lives alongside the "Add custom" tab in the same dialog shell).
 */
export function DrinkPickerList({ onSelect, excludeIds }: DrinkPickerListProps) {
  const [q, setQ] = useState("");
  const { data, isLoading, error, refetch } = useDrinks({ status: "active", q, limit: 20 });
  const items = useMemo(() => {
    const all = data?.pages.flatMap((page) => page.items) ?? [];
    if (!excludeIds || excludeIds.length === 0) return all;
    return all.filter((d) => !excludeIds.includes(d.id));
  }, [data, excludeIds]);

  return (
    <div className="space-y-4">
      <FormField
        label="Search the library"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Drink name..."
      />

      <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
        {items.length === 0 ? (
          <EmptyState
            icon="🍸"
            title="No active drinks found"
            description="Add drinks to the library first, or mark one active."
          />
        ) : (
          <ul className="max-h-80 divide-y divide-border overflow-y-auto">
            {items.map((drink) => (
              <li key={drink.id}>
                <button
                  type="button"
                  data-drink-id={drink.id}
                  onClick={() => onSelect(drink)}
                  className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-3 text-left hover:bg-bg-hover"
                >
                  <span className="font-medium text-text-primary">{drink.name}</span>
                  <span className="flex items-center gap-2">
                    <Badge variant="secondary" size="sm">
                      {SPIRIT_BASE_LABELS[drink.spirit_base]}
                    </Badge>
                    {drink.has_na_variant && (
                      <Badge variant="success" size="sm">
                        NA ✓
                      </Badge>
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </QueryStateBoundary>
    </div>
  );
}
